import { HowToAnswer } from "./HowToAnswer";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, ArrowDown, Check } from "lucide-react";

/**
 * CardSortRank — rank a small set of cards into a single ordered list.
 *
 * Interaction: cards start unranked in a tray. Tap a card to promote it
 * into the ranked column. Once ranked, tap ↑ / ↓ to reorder. Keyboard
 * arrows work when the card is focused.
 *
 * Drag is intentionally not the primary path — on mobile it fights scroll,
 * and there's no keyboard equivalent for drag. Tap + arrows is complete.
 *
 * The learner must rank all cards before the Done button unlocks. That's
 * the discovery signal: forced ranking of "workdays" reveals preference
 * where a "how much did you do this?" bucket would not.
 */

export interface RankItem {
  id: string;
  label: string;
  hint?: string;
}

export interface CardSortRankProps {
  items: RankItem[];
  /** Called once every item is ranked, in order (rank 0 = top). */
  onComplete: (orderedIds: string[]) => void;
  ctaLabel?: string;
  prompt?: string;
}

export function CardSortRank({ items, onComplete, ctaLabel = "Done", prompt }: CardSortRankProps) {
  const [ranked, setRanked] = useState<string[]>([]);
  const remaining = items.filter((it) => !ranked.includes(it.id));
  const allRanked = remaining.length === 0;

  function promote(id: string) {
    setRanked((r) => [...r, id]);
  }

  function move(id: string, dir: -1 | 1) {
    setRanked((r) => {
      const idx = r.indexOf(id);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= r.length) return r;
      const next = [...r];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  function demote(id: string) {
    setRanked((r) => r.filter((x) => x !== id));
  }

  return (
    <div className="space-y-6">
      <HowToAnswer>Tap cards in order, most like you first. Use the ↑ ↓ arrows to reorder.</HowToAnswer>
      {prompt && (
        <p className="text-sm text-muted-foreground">{prompt}</p>
      )}

      {/* Ranked column. */}
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
          Your order — top is most you
        </div>
        <ol className="space-y-2">
          <AnimatePresence>
            {ranked.map((id, i) => {
              const item = items.find((x) => x.id === id)!;
              return (
                <motion.li
                  key={id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.22 }}
                  className="flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary/5 p-3"
                >
                  <div className="shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center text-sm font-medium tabular-nums">
                    {i + 1}
                  </div>
                  <div className="flex-1 text-sm md:text-base">{item.label}</div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => move(id, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                      className="h-9 w-9 grid place-items-center rounded-full border border-border
                        text-muted-foreground hover:text-foreground hover:bg-secondary
                        disabled:opacity-30 disabled:pointer-events-none
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => move(id, 1)}
                      disabled={i === ranked.length - 1}
                      aria-label="Move down"
                      className="h-9 w-9 grid place-items-center rounded-full border border-border
                        text-muted-foreground hover:text-foreground hover:bg-secondary
                        disabled:opacity-30 disabled:pointer-events-none
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => demote(id)}
                      aria-label="Remove from list"
                      className="ml-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1"
                    >
                      Remove
                    </button>
                  </div>
                </motion.li>
              );
            })}
          </AnimatePresence>
          {ranked.length === 0 && (
            <li className="text-sm text-muted-foreground italic">
              Tap a card below to start your ranking.
            </li>
          )}
        </ol>
      </div>

      {/* Unranked tray. */}
      {remaining.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
            Tap to add to your ranking
          </div>
          <div className="flex flex-wrap gap-2">
            <AnimatePresence>
              {remaining.map((it) => (
                <motion.button
                  key={it.id}
                  layout
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  transition={{ duration: 0.18 }}
                  onClick={() => promote(it.id)}
                  className="text-left px-4 py-2.5 rounded-2xl border border-border bg-card text-sm md:text-base
                    min-h-[44px] hover:border-primary/60 hover:bg-primary/5 transition
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {it.label}
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button
          onClick={() => allRanked && onComplete(ranked)}
          disabled={!allRanked}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            disabled:opacity-40 disabled:pointer-events-none inline-flex items-center gap-2
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Check className="h-4 w-4" /> {ctaLabel}
        </button>
      </div>
    </div>
  );
}
